import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { MarketingNav, MarketingFooter } from '../../components/MarketingChrome';
import { Reveal } from '../../components/Reveal';
import { RuleChip } from '../../components/RuleChip';
import step1 from '../../assets/how-it-works/step1-login.jpg';
import step2 from '../../assets/how-it-works/step2-new-permit.jpg';
import step3 from '../../assets/how-it-works/step3-conflict.jpg';
import step4 from '../../assets/how-it-works/step4-override.jpg';
import step5 from '../../assets/how-it-works/step5-audit.jpg';

const STEPS = [
  {
    n: 1,
    title: "Sign in with the role you'd actually hold on-site",
    body: 'Every account carries a real role — Org Admin, Safety Officer, Area Authority, Performing Authority, or Viewer — and every action in the product is gated by it, both in the UI and, non-negotiably, on the server.',
    img: step1,
    alt: 'Real screenshot of the role-based sign-in screen',
  },
  {
    n: 2,
    title: 'Draft the permit the way a requester actually writes it',
    body: 'Free-text scope, a permit type, a work location, and a planned window. Nothing about the conflict engine is visible yet — this step is deliberately just data capture.',
    img: step2,
    alt: 'Real screenshot of the new permit form',
  },
  {
    n: 3,
    title: 'Run the evaluation — deterministic, not generative',
    body: (
      <>
        The PostGIS-backed spatial engine, the topological isolation graph, and the temporal
        (fire-watch / gas-test expiry) rules all run in-process against the live plant model. A
        finding like <RuleChip code="HOT-WORK-RADIUS-01" /> either fires or it doesn't — there is no
        model in that decision path.
      </>
    ),
    img: step3,
    alt: 'Real screenshot of a critical stop-work conflict finding on permit PTW-2002',
  },
  {
    n: 4,
    title: 'Explain it in plain language, override it only with two signatures',
    body: 'An LLM can translate the structured finding into a paragraph a foreman can read at 6am — but it never resolves anything. A CRITICAL_STOP_WORK finding can only be overridden by a documented justification plus an independent second authorization from someone other than the requester.',
    img: step4,
    alt: 'Real screenshot of the dual sign-off override request modal',
  },
  {
    n: 5,
    title: 'Everything is chained, permanently',
    body: 'Every evaluation, override, isolation change, and permit status transition is appended to a SHA-256 hash-chained audit log. Anyone with the right role can recompute the whole chain on demand and get a definitive yes/no on whether it has ever been altered.',
    img: step5,
    alt: 'Real screenshot of the tamper-evident, hash-chained audit log',
  },
];

export default function HowItWorksPage() {
  return (
    <div>
      <MarketingNav />
      <section className="section container-narrow">
        <Reveal>
          <div className="eyebrow">How it works</div>
          <h1 className="h1-hero" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>From scope text to an audit-proof decision, in five steps.</h1>
          <p className="body-text" style={{ marginTop: 16, fontSize: '1.0625rem' }}>
            This walkthrough follows one real permit end to end. Every rule referenced below is
            enforced in the actual conflict engine — none of it is aspirational.
          </p>
        </Reveal>

      </section>

      <section className="section-tight container-narrow" style={{ paddingTop: 0 }}>
        <div className="stack gap-6">
          {STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i % 2}>
              <div className="grid-2" style={{ alignItems: 'center', gap: 40 }}>
                <div style={{ order: i % 2 === 1 ? 2 : 1 }}>
                  <img src={step.img} alt={step.alt} style={{ width: '100%', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-elevated)' }} />
                </div>
                <div style={{ order: i % 2 === 1 ? 1 : 2 }}>
                  <div className="eyebrow">Step {step.n}</div>
                  <h2 className="h3" style={{ fontSize: '1.5rem', marginTop: 8 }}>{step.title}</h2>
                  <p className="body-text" style={{ marginTop: 12 }}>{step.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section container" style={{ textAlign: 'center', background: 'var(--surface-alt)' }}>
        <Reveal>
          <h2 className="h2-section">See it run against a real seeded refinery.</h2>
          <div className="hero-cta-row" style={{ justifyContent: 'center' }}>
            <Link to="/login" className="btn btn-primary">Launch the demo <ArrowRight size={16} /></Link>
            <Link to="/faq" className="btn btn-secondary">Read the FAQ</Link>
          </div>
        </Reveal>
      </section>

      <MarketingFooter />
    </div>
  );
}
